/**
 * Generation History Modal — paginated generation history (auth-required)
 * Ported from studio: uses workspace store + api/prompts
 */

import { store } from '../../core/store.js'
import { fetchGenerationHistory } from '../../api/prompts.js'
import { showToast } from '../../utils/toast.js'
import { esc, escAttr, safeImageUrl } from '../../utils/escape.js'

const MODEL_NAMES = {
  'fal-flux-dev': 'Basic (Legacy)',
  'fal-flux-2-dev': 'Premium',
  'fal-flux-2-edit': 'Basic',
}

const TIER_CLASSES = {
  basic: 'gh-tag-basic',
  premium: 'gh-tag-premium',
}

function getModelName(modelId) {
  return MODEL_NAMES[modelId] || modelId
}

function timeAgo(dateStr) {
  const diff = Date.now() - new Date(dateStr + 'Z').getTime()
  const mins = Math.floor(diff / 60000)
  if (mins < 1) return 'just now'
  if (mins < 60) return `${mins}m ago`
  const hrs = Math.floor(mins / 60)
  if (hrs < 24) return `${hrs}h ago`
  const days = Math.floor(hrs / 24)
  if (days < 30) return `${days}d ago`
  return new Date(dateStr).toLocaleDateString()
}

function renderRow(r) {
  const model = getModelName(r.model_id)
  const tierClass = TIER_CLASSES[r.tier] || 'gh-tag-basic'
  const costLabel = `-${Number(r.credits_cost) || 0}`
  const thumb = safeImageUrl(r.image_url || r.source_image_url)
  const promptSnippet = String(r.final_prompt || '').substring(0, 60)

  return `<div class="gh-row">
    <div class="gh-thumb">${thumb ? `<img src="${escAttr(thumb)}" alt="" loading="lazy" />` : '<div class="gh-no-img"></div>'}</div>
    <div class="gh-info">
      <div class="gh-info-top">
        <span class="gh-tag ${escAttr(tierClass)}">${esc(model)}</span>
        <span class="gh-cost">${esc(costLabel)} credits</span>
        <span class="gh-time">${esc(timeAgo(r.created_at))}</span>
      </div>
      <div class="gh-prompt">${esc(promptSnippet || 'No prompt')}</div>
    </div>
  </div>`
}

let currentOffset = 0
let allLoaded = false

// Inject CSS once
let cssInjected = false
function injectCSS() {
  if (cssInjected) return
  cssInjected = true
  const style = document.createElement('style')
  style.textContent = GH_CSS
  document.head.appendChild(style)
}

export async function showHistoryModal() {
  const customer = store.getState('auth.customer')
  if (!customer?.id) {
    showToast('Please login first', 'info')
    return
  }

  injectCSS()
  currentOffset = 0
  allLoaded = false

  // Create modal
  let modal = document.getElementById('wsHistoryModal')
  if (!modal) {
    modal = document.createElement('div')
    modal.id = 'wsHistoryModal'
    modal.className = 'gh-overlay'
    document.body.appendChild(modal)
  }

  modal.innerHTML = `
    <div class="gh-modal">
      <div class="gh-header">
        <h3 class="gh-title">Generation History</h3>
        <button class="gh-close" id="ghClose">&times;</button>
      </div>
      <div class="gh-list" id="ghList">
        <div class="gh-loading">Loading...</div>
      </div>
      <button class="gh-load-more hidden" id="ghLoadMore">Load more</button>
    </div>`

  modal.classList.add('show')

  modal.querySelector('#ghClose').onclick = hideHistoryModal
  if (!modal.dataset.handlersBound) {
    modal.dataset.handlersBound = 'true'
    modal.addEventListener('click', (e) => {
      if (e.target === modal) hideHistoryModal()
    })
  }
  modal.querySelector('#ghLoadMore').onclick = () => loadMore()

  await loadHistory()
}

async function loadHistory() {
  const list = document.getElementById('ghList')
  const customer = store.getState('auth.customer')
  try {
    const records = await fetchGenerationHistory(customer.id, 20, currentOffset)
    if (records.length < 20) allLoaded = true

    if (currentOffset === 0 && records.length === 0) {
      list.innerHTML = '<div class="gh-empty">No generations yet</div>'
      return
    }

    const html = records.map(renderRow).join('')
    if (currentOffset === 0) {
      list.innerHTML = html
    } else {
      list.insertAdjacentHTML('beforeend', html)
    }

    currentOffset += records.length
    const btn = document.getElementById('ghLoadMore')
    if (btn) btn.classList.toggle('hidden', allLoaded)
  } catch (e) {
    if (currentOffset === 0) list.innerHTML = '<div class="gh-empty">Failed to load history</div>'
    showToast('Failed to load history', 'error')
  }
}

async function loadMore() {
  const btn = document.getElementById('ghLoadMore')
  if (btn) btn.textContent = 'Loading...'
  await loadHistory()
  if (btn) btn.textContent = 'Load more'
}

export function hideHistoryModal() {
  const modal = document.getElementById('wsHistoryModal')
  if (modal) modal.classList.remove('show')
}

const GH_CSS = `
.gh-overlay{position:fixed;inset:0;background:var(--bg-overlay);backdrop-filter:blur(12px);z-index:9000;display:flex;align-items:center;justify-content:center;opacity:0;pointer-events:none;transition:opacity .3s}
.gh-overlay.show{opacity:1;pointer-events:auto}
.gh-modal{background:var(--surface-s3);border:1px solid var(--border-subtle);border-radius:16px;width:100%;max-width:520px;max-height:80vh;overflow:hidden;display:flex;flex-direction:column;font-family:var(--font-body);color:var(--text-primary)}
.gh-header{display:flex;align-items:center;justify-content:space-between;padding:16px 20px;border-bottom:1px solid var(--border-subtle)}
.gh-title{font-size:16px;font-weight:600;margin:0}
.gh-close{background:none;border:none;color:var(--gray-500);font-size:22px;cursor:pointer;padding:4px 8px;border-radius:6px;transition:all .2s}
.gh-close:hover{color:var(--text-primary);background:var(--surface-s2)}
.gh-list{overflow-y:auto;flex:1;padding:8px 0}
.gh-row{display:flex;gap:12px;padding:10px 20px;transition:background .2s;cursor:default}
.gh-row:hover{background:var(--surface-s1)}
.gh-thumb{width:48px;height:48px;border-radius:8px;overflow:hidden;flex-shrink:0;background:var(--surface-s1)}
.gh-thumb img{width:100%;height:100%;object-fit:cover}
.gh-no-img{width:100%;height:100%;background:var(--surface-s2)}
.gh-info{flex:1;min-width:0}
.gh-info-top{display:flex;align-items:center;gap:8px;margin-bottom:4px;flex-wrap:wrap}
.gh-tag{font-size:10px;padding:2px 6px;border-radius:4px;font-weight:600;text-transform:uppercase}
.gh-tag-basic{background:color-mix(in oklch, var(--brand-pillar-everywhere) 12%, transparent);color:var(--brand-pillar-everywhere)}
.gh-tag-premium{background:color-mix(in oklch, var(--brand-pillar-artist) 12%, transparent);color:var(--brand-pillar-artist)}
.gh-cost{font-size:11px;color:var(--gray-500)}
.gh-time{font-size:11px;color:var(--gray-400);margin-left:auto}
.gh-prompt{font-size:12px;color:var(--gray-500);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
.gh-loading,.gh-empty{text-align:center;padding:40px 20px;color:var(--gray-500);font-size:13px}
.gh-load-more{display:block;width:calc(100% - 40px);margin:8px 20px 16px;padding:10px;background:var(--surface-s1);border:1px solid var(--border-subtle);border-radius:8px;color:var(--text-muted);font-size:13px;cursor:pointer;transition:all .2s}
.gh-load-more:hover{background:var(--surface-s2);color:var(--text-primary)}
.gh-load-more.hidden{display:none}
`
